/**
 * The Payments tab: every payment a customer has made on the job, and the
 * door to enter one by hand (ADR 0288).
 *
 * Jonathon, 2026-10-14: "there needs to be a payments tab on each job where
 * we can see the payments the customer has made and we need the ability to
 * enter in payments manually."
 *
 * The list is `paymentHistory` in packages/core, so a check and its deposit,
 * or a card payment and its credit, are one row and not two. This file draws
 * it and offers the three things a person does to a payment: enter one, say
 * one is in the bank, and remove one entered by mistake, with a reason.
 *
 * "It's in the bank" is on every payment still on its way, a paid link
 * included (ADR 0312). Jonathon, 2026-10-26: "say a payment is already in
 * the bank and it updates that automatically instead of waiting."
 */

import {
  formatMoney,
  paymentHistoryTotals,
  receiptDate,
  type PaymentHistoryRow,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";

export function PaymentsCard({
  rows,
  onRecord,
  onInTheBank,
  onRemove,
  canRemove,
}: {
  rows: readonly PaymentHistoryRow[];
  onRecord: () => void;
  /** A person saying this row's money is in the bank: a recorded payment, or a paid link. */
  onInTheBank: (
    row: { recordedId: string } | { linkId: string },
  ) => Promise<void>;
  onRemove: (paymentId: string, reason: string) => Promise<void>;
  /**
   * Whether this login may remove a payment: admins only, the same people
   * the table's delete rule lets delete one (Josh, 2026-10-23). Anyone else
   * would be shown a button that always fails.
   */
  canRemove: boolean;
}) {
  const [removing, setRemoving] = useState<PaymentHistoryRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const totals = paymentHistoryTotals(rows);

  const [banking, setBanking] = useState<string | null>(null);

  const inTheBank = async (row: PaymentHistoryRow) => {
    setError(null);
    setBanking(row.key);
    try {
      if (row.recordedId) await onInTheBank({ recordedId: row.recordedId });
      else if (row.linkId) await onInTheBank({ linkId: row.linkId });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBanking(null);
    }
  };

  return (
    <div className="block bal">
      <div className="bal-head">
        <h3>{t("Payments")}</h3>
        <button type="button" className="btn-small" onClick={onRecord}>
          {t("Record a payment")}
        </button>
      </div>
      <ErrorNote error={error} />

      {rows.length === 0 ? (
        <p className="na-hint">{t("No payments yet.")}</p>
      ) : (
        <table className="grid bal-rows" aria-label={t("Payments")}>
          <thead>
            <tr>
              <th>{t("Date")}</th>
              <th>{t("How")}</th>
              <th>{t("For")}</th>
              <th className="bal-amt">{t("Amount")}</th>
              <th>{t("Status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={row.inTheBank ? "bal-in" : ""}>
                <td>{row.date ? receiptDate(row.date) : "—"}</td>
                <td>
                  {t(row.how)}
                  {row.receiptNumber && (
                    <div className="na-hint">
                      {t("Receipt {number}", { number: row.receiptNumber })}
                    </div>
                  )}
                </td>
                <td>{t(row.forWhat)}</td>
                <td className="bal-amt">{row.amountFormatted}</td>
                <td className="bal-state">
                  {row.inTheBank ? t("In the bank") : t("On its way")}
                </td>
                <td>
                  {row.awaitsAPerson && (row.recordedId || row.linkId) && (
                    <button
                      type="button"
                      className="btn-small"
                      disabled={banking === row.key}
                      onClick={() => void inTheBank(row)}
                    >
                      {t("It’s in the bank")}
                    </button>
                  )}
                  {row.recordedId && canRemove && (
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => setRemoving(row)}
                    >
                      {t("Remove")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="bal-total">
              <td colSpan={3}>{t("In the bank")}</td>
              <td className="bal-amt">{formatMoney(totals.inTheBank)}</td>
              <td className="bal-state">
                {totals.onItsWay > 0
                  ? t("{amount} on its way", {
                      amount: formatMoney(totals.onItsWay),
                    })
                  : ""}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      )}

      <div className="na-hint">
        {t(
          "Card and bank payments made through a payment link appear here by themselves. Payments taken any other way are entered with Record a payment; only those can be removed. It’s in the bank counts a payment now instead of waiting for Mercury to show it.",
        )}
      </div>

      {removing && (
        <RemovePayment
          row={removing}
          onClose={() => setRemoving(null)}
          onRemove={async (reason) => {
            await onRemove(removing.recordedId as string, reason);
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
}

/** Asks why before a payment goes. The reason is kept on the job. */
function RemovePayment({
  row,
  onClose,
  onRemove,
}: {
  row: PaymentHistoryRow;
  onClose: () => void;
  onRemove: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (!reason.trim()) {
      setError(t("Say why the payment is being removed."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onRemove(reason.trim());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("Remove this payment?")}
      subtitle={`${row.amountFormatted} ${t(row.how).toLowerCase()}`}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void remove()}
            disabled={busy}
          >
            {busy ? t("Removing…") : t("Remove the payment")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="field">
        <label htmlFor="remove-reason">{t("Why is it being removed?")}</label>
        <textarea
          id="remove-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <p className="na-hint">
        {t(
          "It comes off the balance. The job’s history keeps who removed it, when, and why. A receipt already emailed is not taken back.",
        )}
      </p>
    </Modal>
  );
}
